/**
 * Hook compartilhado para obter a contagem mensal de cobranças ativas do
 * tenant — usado pelo controle de limite gratuito (10 cobranças/mês sem
 * o add-on Módulo Financeiro).
 *
 * RELEASE: 2026-06-16 v1.0.0 - Versão inicial. Centraliza a query que antes
 *          era duplicada em FinancialCharges, FinancialSubscriptions e
 *          FinancialInstallments. Agora também é usada na aba Financeiro
 *          de Configurações (FinancialSettingsCard).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useFinancialChargeCount');

export interface UseFinancialChargeCountResult {
  /** Quantidade de cobranças ativas contadas no mês corrente (TZ do tenant). */
  chargeCount: number;
  isLoading: boolean;
}

/**
 * Conta cobranças ativas via RPC `count_active_financeiro_cobrancas`.
 * Cacheado por 30s no TanStack Query para reduzir round-trips.
 */
export function useFinancialChargeCount(): UseFinancialChargeCountResult {
  const { currentTenant } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['financeiro-cobrancas-count', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'count_active_financeiro_cobrancas' as never,
      );
      if (error) {
        logger.error('Erro ao contar cobranças ativas', { error });
        return 0;
      }
      return (data as number) ?? 0;
    },
    staleTime: 30_000,
    enabled: !!currentTenant?.id,
  });

  return { chargeCount: data ?? 0, isLoading };
}

export default useFinancialChargeCount;
